// ── useSynapses: client hook for synapse management ─────────
// Wraps the /api/synapses/* endpoints.

import { useCallback } from 'react';

export default function useSynapses({ sessionId, authFetch }) {
  const createSynapse = useCallback(async ({ source, target, type, weight, label }) => {
    const res = await authFetch('/api/synapses/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, source, target, type, weight, label }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Failed to create synapse' }));
      throw new Error(err.error);
    }
    return res.json();
  }, [sessionId, authFetch]);

  const updateSynapse = useCallback(async (synapseId, { weight, label }) => {
    const res = await authFetch('/api/synapses/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, synapseId, weight, label }),
    });
    if (!res.ok) throw new Error('Failed to update synapse');
    return res.json();
  }, [sessionId, authFetch]);

  const deleteSynapse = useCallback(async (synapseId) => {
    const res = await authFetch('/api/synapses/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, synapseId }),
    });
    if (!res.ok) throw new Error('Failed to delete synapse');
    return res.json();
  }, [sessionId, authFetch]);

  const getNodeSynapses = useCallback(async (nodeId) => {
    const res = await authFetch(`/api/synapses/${sessionId}/${nodeId}`);
    if (!res.ok) throw new Error('Failed to load synapses');
    return res.json();
  }, [sessionId, authFetch]);

  const getAllSynapses = useCallback(async () => {
    const res = await authFetch(`/api/synapses/${sessionId}`);
    if (!res.ok) throw new Error('Failed to load synapses');
    return res.json();
  }, [sessionId, authFetch]);

  const suggestSynapses = useCallback(async () => {
    const res = await authFetch('/api/synapses/suggest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    });
    if (!res.ok) throw new Error('Failed to get suggestions');
    return res.json();
  }, [sessionId, authFetch]);

  const detectCoActivation = useCallback(async () => {
    const res = await authFetch('/api/synapses/hebbian', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    });
    if (!res.ok) throw new Error('Failed to detect co-activation');
    return res.json();
  }, [sessionId, authFetch]);

  return {
    createSynapse,
    updateSynapse,
    deleteSynapse,
    getNodeSynapses,
    getAllSynapses,
    suggestSynapses,
    detectCoActivation,
  };
}
